import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ITestDefinition } from '../../models/testDefinition';
import { RoleSeriesPoint } from '../hooks/useTeamAnalytics';
import styles from './RoleComparisonChart.module.scss';

const ROLE_COLORS: string[] = [
  '#013344',
  '#27ae60',
  '#5BC9E0',
  '#e67e22',
  '#9b59b6',
  '#f57c73',
  '#FFD15A',
  '#34495e',
];

interface Props {
  categoryTests: ITestDefinition[];
  roleSeriesMap: Record<string, RoleSeriesPoint[]>;
  allRoles: string[];
  selectedRoles: string[];
  onRoleToggle: (role: string) => void;
}

export default function RoleComparisonChart({
  categoryTests,
  roleSeriesMap,
  allRoles,
  selectedRoles,
  onRoleToggle,
}: Props) {
  const activeRoles = selectedRoles.length > 0 ? selectedRoles : allRoles;

  if (allRoles.length === 0) {
    return (
      <div className={styles.noRoles}>
        Nessun ruolo/posizione impostato per gli atleti del team. Aggiungi il campo "posizione" negli
        impostazioni atleta per abilitare il confronto per ruolo.
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* Role filter */}
      <div className={styles.filterRow}>
        <span className={styles.filterLabel}>Ruoli:</span>
        {allRoles.map((role, idx) => {
          const color = ROLE_COLORS[idx % ROLE_COLORS.length];
          const isActive = selectedRoles.length === 0 || selectedRoles.includes(role);
          return (
            <button
              key={role}
              onClick={() => onRoleToggle(role)}
              className={[styles.roleChip, isActive ? styles.roleChipActive : ''].filter(Boolean).join(' ')}
              style={isActive ? { borderColor: color, color, background: `${color}18` } : undefined}
            >
              {role}
            </button>
          );
        })}
      </div>

      {/* One chart per test */}
      <div className={styles.chartsStack}>
        {categoryTests.map((test) => {
          const data = roleSeriesMap[test.id] ?? [];
          if (data.length === 0) return null;

          return (
            <div key={test.id} className={styles.chartCard}>
              <div className={styles.chartHeader}>
                <p className={styles.chartTitle}>{test.name}</p>
                <p className={styles.chartSubtitle}>Media mensile per ruolo</p>
              </div>
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi',sans-serif" }}
                      tickLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis
                      domain={['auto', 'auto']}
                      tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi',sans-serif" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--c-card-bg)',
                        border: '1px solid #e7e7e7',
                        borderRadius: '8px',
                        fontFamily: "'Satoshi',sans-serif",
                        fontSize: '12px',
                      }}
                    />
                    <Legend
                      wrapperStyle={{ fontFamily: "'Satoshi',sans-serif", fontSize: '12px' }}
                    />
                    {activeRoles.map((role, idx) => {
                      const color = ROLE_COLORS[allRoles.indexOf(role) % ROLE_COLORS.length] ?? ROLE_COLORS[idx % ROLE_COLORS.length];
                      return (
                        <Line
                          key={role}
                          type="monotone"
                          dataKey={role}
                          stroke={color}
                          strokeWidth={2}
                          dot={{ fill: color, r: 4 }}
                          activeDot={{ r: 6 }}
                          connectNulls
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
